import { RefreshCw, Send } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Steps, Callout, pathFor,
  Diagram, MockField, MockSelect, MockButton, MockRow,
} from "../shared";

export default function PagesUpdatesSection() {
  return (
    <div data-testid="guide-pages-updates">
      <PageHeading
        icon={RefreshCw}
        title="Updates"
        description="The day-to-day working view. Members see the items they need to act on; admins see everything. Each row shows the original instruction, the latest progress note, and quick controls right where you need them."
      />

      <Callout variant="info" title="In plain words">
        This is your <span className="text-white">to-do list with notes</span>. You don't have to open each item separately — you can read the instruction, jot a progress note, and move work along, all without leaving the page.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1.5 ml-2">
          <li>Every day, to add progress notes on the items you're working on.</li>
          <li>To send a finished task into the review queue with one click.</li>
          <li>To check whether a reviewer has handed something back to you for changes.</li>
        </ul>
      </Section>

      <Section title="A picture of the page">
        <Diagram
          frameLabel="Updates"
          legend={[
            <>The <span className="text-white">search box</span> matches title, instruction, owner, lane, and origin reference; the filter chips narrow by status, lane, or overdue.</>,
            <>Each row can <span className="text-white">expand</span> to show the instruction, sub-tasks (with a "2/5 done" progress chip), and the progress timeline. Type into the inline box to add an update without leaving the page.</>,
            <>A green <span className="inline-flex items-center gap-1"><Send className="w-3 h-3 text-emerald-300" /> Send for Review</span> button on every in-progress task pushes it into the MD review queue in one click.</>,
            <>Admins also get a <span className="text-white">status selector</span> and a <span className="text-white">Hand to</span> dropdown inline.</>,
          ]}
        >
          <MockRow>
            <MockField placeholder="Search title, instruction, owner, lane…" marker={1} />
            <MockSelect value="All statuses" />
          </MockRow>
          <div className="rounded-md border border-white/10 bg-white/[0.03] p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-white/80">Q3 portfolio pack</span>
              <span className="text-2xs text-white/40">2/5 done</span>
            </div>
            <MockField label="Add a progress note" placeholder="What did you do?" marker={2} />
            <MockRow>
              <MockButton label="Send for Review" marker={3} variant="ghost" />
              <MockSelect label="Hand to" value="Choose a person…" marker={4} />
            </MockRow>
          </div>
        </Diagram>
        <Steps
          steps={[
            <>Use the <span className="text-white">search box and filter chips</span> to find what you need. Members are filtered to their own items automatically.</>,
            <>Open a row and type into the <span className="text-white">inline note box</span> to record progress, then save. Watch the sub-task chip to see how many are done.</>,
            <>Press <span className="text-white">Send for Review</span> on an in-progress task to move it into the MD queue — this records the status change and handoff together in one audit event.</>,
            <>If you're an admin, change the <span className="text-white">status</span> or <span className="text-white">Hand to</span> someone directly from the row.</>,
          ]}
        />
      </Section>

      <Callout variant="warning" title="Returned for amendments">
        If a row shows an <span className="text-orange-300">orange "Returned for Amendments"</span> banner, a reviewer has sent it back with a reason. Read the reason, make the fix, and add an update so the reviewer knows it's ready again.
      </Callout>

      <Callout variant="info">
        The <span className="text-white">Hand to</span> dropdown leaves out ceremonial workflow stages (SDF MD, SDF PMO, and so on) — those aren't people you assign to, they're routing destinations reached via Send for Review or a status change. For the note-adding walkthrough, see <Link href={pathFor("forms/update")} className="text-cyan-300 hover:underline">Add a Progress Update</Link>; for the queue step, see <Link href={pathFor("forms/send-for-review")} className="text-cyan-300 hover:underline">Send for Review</Link>.
      </Callout>
    </div>
  );
}
